import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getStoredPageSize } from '../components/Pagination';
import { sortHireRows } from '../components/hires/HireTableView';
import { applySortClick, hireInitialSortDir } from '../utils/tableSort';
import { bucketHires, hireTableHomeTab, hireTableTabRows, HIRE_TABLE_TABS } from '../utils/hireBuckets';
import { withDirtyRowPinned } from '../utils/withDirtyRowPinned';
import { useToastOrNoop } from '../contexts/ToastContext';
import { scrollUnderPinnedChrome } from './useOpenAtTop';

// A hire's own Table tab now lives in `utils/hireBuckets.js` (2026-09-29): the
// Completed tab made "which tab" depend on the ORDER as well as the bucket, so
// the rule sits beside the tab membership it must agree with.
export { TAB_FOR_BUCKET } from '../utils/hireBuckets';

const hireRowKey = (row) => row.item_id;
const EMPTY_IDS = new Set();

/**
 * THE hires Table's LIST state — the status tab, the header sort, the page,
 * the page size, the dirty-row pin and the `?hire=` deep-link resolution —
 * shared by `pages/DepartmentHires.js` and `pages/MyHires.js` (2026-09-27).
 * Owner: *"wheres the same show 25,50,100,all ?? to speed up the page?"*, then
 * *"Check whatelse you missed"*.
 *
 * 🔑 LIFTED OUT OF `components/hires/HireTableView.js` ON PURPOSE. The tab and
 * sort were local state inside the table; three PAGE features need them —
 * the Actions ▾ export and print ("the rows CURRENTLY SHOWN": this tab + the
 * search, every page), the status tabs living in the page's chrome, and the
 * deep link, which must pick a tab and a page. The table now renders what it
 * is handed and decides nothing about which rows those are.
 *
 * ── Pagination — the ORDER PAGES' rules, not new ones ──────────────────
 * - `components/Pagination` + `getStoredPageSize`: the ONE global
 *   `sfx_page_size` (25/50/100/All). 'ALL' is never remembered (Pagination's
 *   own ruling), so it lasts until the user leaves.
 * - `effectivePageSize = isMobile ? 50 : pageSize` — `DepartmentOrders.js`'s
 *   own expression; Pagination's size picker is `hidden md:flex`, so a phone
 *   could not change it anyway.
 * - 'ALL' is handled BEFORE any arithmetic (root CLAUDE.md, Pagination:
 *   `slice(NaN, NaN)` is empty).
 * - The page resets to 1 on a TAB or SEARCH change — the order pages' reset
 *   effect, keyed on the same kind of inputs. Sorting does NOT reset it, as
 *   there.
 * - `utils/withDirtyRowPinned.js`, exactly as the order pages: a hire row whose
 *   expanded order holds UNSAVED edits is never sliced away — by paging, a tab
 *   change, a search, or a silent refetch that re-buckets it. Sourced from the
 *   UNFILTERED `allItems`, keyed by `item_id` (a hire row has no `id`).
 *   🪤 The pin alone is not enough: the PAGE must also not swap the table for
 *   an EmptyState while `pageRows` is non-empty (a search that hides every row
 *   still holds the pinned one) — both hires pages gate their empty branches on
 *   `list.pageRows.length === 0`, exactly as Dept Orders gates on
 *   `paginatedOrders.length === 0`. (Review round 3, F1: it said "never" while
 *   a search that hid everything unmounted the edit.)
 *   🔒 The VIEW switch (Table → Kanban/Gantt/Calendar) and, since 2026-09-28,
 *   a status-TAB click are the guarded paths: both pages route them through
 *   the row expansion's `continueWhenClean` (via `HireOrderExpansion`'s
 *   `continueWhenCleanRef`), which ASKS the three-way prompt while a row is
 *   dirty and then switches. (Review round 4: it silently discarded the edit;
 *   a pulse then refused it invisibly.) The pin still covers a tab change
 *   made any other way (the deep link) and every refetch. A failed SILENT
 *   refetch also keeps the table mounted (error above it, never instead).
 * - The page is CLAMPED to the last page (see `totalPages` below).
 *   `totalItems` stays on the tab's rows: the pinned row is an extra held on
 *   screen, not a member of the set, and counting it would make the pager lie.
 *
 * ── Deep link ──────────────────────────────────────────────────────────
 * `requestItem(itemId)` (the page's `useDeepLinkParam('hire', …)`) mirrors
 * `DepartmentOrders.js`'s `?order=` resolve: found in this tab → jump to its
 * page and reveal it; not found but present → select ITS OWN tab (the order
 * page lands on the order's own queue, same ruling), clear the search, say so
 * once; absent altogether → an error toast, never silence.
 * 🚨 The resolve effect MUST stay declared AFTER the page-reset effect — the
 * same hook-order trap `DepartmentOrders.js` documents: the reset branch
 * changes the tab/search, both effects re-run in one pass, and the reset must
 * write page 1 FIRST so the jump overwrites it.
 *
 * @param {object} args
 * @param {Array}  args.items        the SEARCH-FILTERED hires (what the tabs count)
 * @param {Array}  args.allItems     every hire loaded — the pin's and the deep link's source
 * @param {string} args.searchTerm   the page's search term (resets the page)
 * @param {() => void} args.clearSearch  the deep link's filter reset
 * @param {boolean} args.isMobile    below 768px → 50 per page
 * @param {number|null} args.dirtyItemId  the hire line whose order form is dirty
 * @param {boolean} args.ready       a real list is on screen to resolve against
 *   (both hires pages pass `!loading && hasLoaded && effectiveTab === 'table'`
 *   since round-4 review F3 — NOT `!error`: an error banner from a failed
 *   SILENT refetch must not park a hire-click resolve until it clears)
 * @param {(item) => void} args.onReveal  the deep link found its row: expand it
 * @param {[string, Function]} [args.tabState]  optional `[tab, setTab]` owned by
 *   the PAGE, so it can hold the tab in the history entry
 *   (`hooks/useViewInHistory`, 2026-10-13). This hook stays router-free: omitted,
 *   it keeps the tab in its own state exactly as before.
 */
export default function useHireTableList({
  items, allItems, searchTerm, clearSearch, isMobile, dirtyItemId = null, ready, onReveal, tabState,
}) {
  const { info: toastInfo, error: toastError } = useToastOrNoop();
  const ownTabState = useState('all');
  const [tab, setTab] = tabState || ownTabState;
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(getStoredPageSize);

  const tabRows = useMemo(() => hireTableTabRows(bucketHires(items)), [items]);
  const tabs = useMemo(
    () => HIRE_TABLE_TABS.map((t) => ({ ...t, count: tabRows[t.id].length })),
    [tabRows]
  );
  /**
   * 📌 THE ROW STAYS PUT (owner, 2026-09-27) — modelled on Department Orders'
   * old `stickyOrderIds` (now `hooks/useHeldOpenRows.js`, a DIFFERENT lifecycle
   * — never released by a reload — that this list deliberately does not use),
   * keyed by `item_id`. An Extend / Off hire / Bring back that
   * moves a hire OUT of the current tab leaves it on screen, where it was, until
   * the user does something that says "show me the list again":
   * - `holdRow(item)` is called by the PAGE after the action's PATCH succeeds and
   *   BEFORE its refetch; on `all` it is a no-op (nothing can leave `All`);
   * - the refetch that follows CONSUMES the hold (`keepNextRef`) — every other
   *   replacement of `allItems` (the 30 s poll, a panel save's refresh) CLEARS
   *   the set, cleared-by-default exactly as `fetchOrders` is;
   * - a tab or a search change clears it (the page-reset effect below);
   * - if the tab or search moved while the action was in flight, no pin and no
   *   toast — the row simply arrives in its true tab (Dept Orders' two
   *   `stillOnTheSameFilters` checks).
   * It is a LINGER, not a membership: the tab COUNTS never include it, and it is
   * ORed into the TAB predicate only — a pinned row the search no longer matches
   * is gone (`items` is the search-filtered list). In the tab's natural order it
   * keeps the index it had; under a header sort it sorts with the rest.
   */
  const [stickyIds, setStickyIds] = useState(EMPTY_IDS);
  const holdRef = useRef(null);
  const lastRowIdsRef = useRef([]);
  const rows = useMemo(() => {
    let base = tabRows[tab];
    if (tab !== 'all' && stickyIds.size) {
      const present = new Set(base.map((r) => r.item_id));
      const extra = items.filter((r) => stickyIds.has(r.item_id) && !present.has(r.item_id));
      if (extra.length) {
        base = [...base];
        extra.forEach((r) => {
          const at = lastRowIdsRef.current.indexOf(r.item_id);
          base.splice(at === -1 ? base.length : Math.min(at, base.length), 0, r);
        });
      }
    }
    return sortHireRows(base, sortField, sortDir);
  }, [tabRows, tab, sortField, sortDir, stickyIds, items]);
  useEffect(() => { lastRowIdsRef.current = rows.map((r) => r.item_id); }, [rows]);

  const holdRow = useCallback((item) => {
    if (!item || tab === 'all') return;
    holdRef.current = { itemId: item.item_id, tab, searchTerm };
    setStickyIds((prev) => (prev.has(item.item_id) ? prev : new Set(prev).add(item.item_id)));
  }, [tab, searchTerm]);

  // A replacement list arrived: the held action's own refetch keeps the pin
  // (and announces the move once); anything else clears it.
  const firstItemsRef = useRef(true);
  useEffect(() => {
    if (firstItemsRef.current) { firstItemsRef.current = false; return; }
    const hold = holdRef.current;
    holdRef.current = null;
    if (!hold || hold.tab !== tab || hold.searchTerm !== searchTerm) {
      setStickyIds((prev) => (prev.size ? EMPTY_IDS : prev));
      return;
    }
    const row = allItems.find((r) => r.item_id === hold.itemId);
    const toTab = row ? hireTableHomeTab(row) : null;
    const stillHere = row && hireTableTabRows(bucketHires([row]))[tab].length > 0;
    if (!row || stillHere || !toTab) return;
    const destination = HIRE_TABLE_TABS.find((t) => t.id === toTab);
    toastInfo(`${row.description || 'This hire'} is now in ${destination ? destination.label : toTab}.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems]);

  const effectivePageSize = isMobile ? 50 : pageSize;
  // 🔑 CLAMPED (review round 3, F2): a refetch that SHRINKS the tab while on a
  // later page must never render past the last page — a blank table under a
  // pager reading "Showing 26-20 of 20". `page` is used for the slice and the
  // pager at once, and the effect below writes it back into state.
  const totalPages = effectivePageSize === 'ALL'
    ? 1
    : Math.max(1, Math.ceil(rows.length / effectivePageSize));
  const page = Math.min(currentPage, totalPages);
  const pageOfRows = effectivePageSize === 'ALL'
    ? rows
    : rows.slice((page - 1) * effectivePageSize, page * effectivePageSize);
  const pageRows = withDirtyRowPinned(pageOfRows, allItems, dirtyItemId, hireRowKey);
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  // 🔀 2026-09-28: `explicitDir`, for the PO/Order CYCLING column
  // (`HIRE_TABLE_COLUMNS`' `po_number`), mirrors `DepartmentOrders.js`'s own
  // `handleSort(field, explicitDir)` — it returns BEFORE the toggle logic
  // below is ever consulted, so a cycling column's fixed-per-field direction
  // is never subject to the same-field-toggles-direction rule a single-field
  // header uses. A single-field column still calls this with ONE argument
  // (`ui/SortableHeader`'s contract), so every existing caller is unchanged.
  // The rule is `utils/tableSort.js#applySortClick`, the order pages' own; a
  // new field here always opens ascending (`hireInitialSortDir`).
  const onHeaderSort = useCallback((field, explicitDir) => applySortClick(field, explicitDir, sortField, {
    setSortField, setSortDir, initialDir: hireInitialSortDir,
  }), [sortField]);

  const onPageSizeChange = useCallback((size) => { setPageSize(size); setCurrentPage(1); }, []);

  // Reset to page 1 on a tab or search change — and drop the sticky rows, the
  // same effect Dept Orders uses for both. MUST precede the resolve effect.
  useEffect(() => {
    setCurrentPage(1);
    setStickyIds((prev) => (prev.size ? EMPTY_IDS : prev));
  }, [searchTerm, tab]);

  const [pendingItemId, setPendingItemId] = useState(null);
  const resetDone = useRef(false);
  const requestItem = useCallback((id) => {
    resetDone.current = false;
    setPendingItemId(id);
  }, []);

  useEffect(() => {
    if (pendingItemId == null || !ready) return;
    const idx = rows.findIndex((r) => r.item_id === pendingItemId);
    if (idx === -1) {
      const target = allItems.find((r) => r.item_id === pendingItemId);
      if (target && !resetDone.current) {
        resetDone.current = true;
        // The toast says WHAT happened (review round 3, F6) — it said "Filters
        // were cleared" when only the tab had changed.
        const nextTab = hireTableHomeTab(target) || 'all';
        const tabChanged = nextTab !== tab;
        const searchCleared = Boolean(searchTerm && searchTerm.trim()) && Boolean(clearSearch);
        setTab(nextTab);
        if (searchCleared) clearSearch();
        const tabLabel = (HIRE_TABLE_TABS.find((t) => t.id === nextTab) || {}).label;
        if (tabChanged && searchCleared) toastInfo(`Switched to “${tabLabel}” and cleared the search to show this hire.`);
        else if (tabChanged) toastInfo(`Switched to “${tabLabel}” to show this hire.`);
        else if (searchCleared) toastInfo('Cleared the search to show this hire.');
        return;
      }
      setPendingItemId(null);
      toastError('That hire could not be found — it may have been removed or belong to a different department.');
      return;
    }
    const item = rows[idx];
    setCurrentPage(effectivePageSize === 'ALL' ? 1 : Math.floor(idx / effectivePageSize) + 1);
    setPendingItemId(null);
    if (onReveal) onReveal(item);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="hire-row-${item.item_id}"]`);
      scrollUnderPinnedChrome(el);
    }, 150);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingItemId, ready, rows, allItems, effectivePageSize]);

  return {
    tab, setTab, tabs, rows, pageRows,
    sortField, sortDir, onHeaderSort,
    currentPage: page, setCurrentPage, pageSize: effectivePageSize, onPageSizeChange,
    requestItem, holdRow,
    // Every hire loaded, unfiltered — the expanded order's hire actions find
    // their lines here (`HireOrderExpansion.js`), never in one tab's rows.
    allItems,
  };
}
